// Visual effects for one room: a pooled sprite-particle engine, telegraph shapes,
// lightning, rings, damage numbers, screen shake / flash (settings-aware) and
// hit flashes. Everything is driven by update(dt), so hitstop and pauses freeze it.

import Phaser from 'phaser';
import { FX_EXTRA } from '../../art/world';
import { TEX } from '../../art/keys';
import { DEPTH, FONT, GAME_HEIGHT, GAME_WIDTH } from '../../config/constants';
import { COLORS, css, mix } from '../../config/palette';
import type { FxApi, GameContext, TelegraphHandle, Vec } from '../../core/contracts';
import type { TrailKind } from '../../core/types';

interface Particle {
  img: Phaser.GameObjects.Image;
  life: number;
  age: number;
  vx: number;
  vy: number;
  drag: number;
  gravity: number;
  s0: number;
  s1: number;
  a0: number;
  a1: number;
  spin: number;
  /** Stretch along velocity (sparks). */
  stretch: number;
  /** Frame stepping for sheet animations (explosion). */
  frames: number;
  /** Per-axis scale multipliers (afterimages keep a captured, possibly mirrored, transform). */
  sx: number;
  sy: number;
}

interface PSpec {
  key: string;
  frame?: string | number;
  x: number;
  y: number;
  vx?: number;
  vy?: number;
  life: number;
  s0?: number;
  s1?: number;
  a0?: number;
  a1?: number;
  tint?: number;
  add?: boolean;
  rot?: number;
  spin?: number;
  drag?: number;
  gravity?: number;
  stretch?: number;
  frames?: number;
  depth?: number;
}

interface Bolt {
  g: Phaser.GameObjects.Graphics;
  points: Vec[];
  color: number;
  width: number;
  life: number;
  age: number;
  jitter: number;
  branches: boolean;
  nextJitter: number;
}

type TeleKind = 'line' | 'circle' | 'rect' | 'marker';
interface Tele extends TelegraphHandle {
  kind: TeleKind;
  g: Phaser.GameObjects.Graphics | null;
  img: Phaser.GameObjects.Image | null;
  p: { x: number; y: number; x2: number; y2: number; radius: number; w: number; h: number; angle: number; width: number };
  color: number;
  age: number;
  dur: number;
  follow?: Phaser.GameObjects.GameObject;
  dead: boolean;
}

interface Num {
  t: Phaser.GameObjects.Text;
  age: number;
  life: number;
  vy: number;
  baseScale: number;
}

const MAX_PARTICLES = 700;

export function createFx(ctx: GameContext): FxApi {
  const scene = ctx.scene;
  const settings = () => ctx.settings;
  const rand = (a: number, b: number) => a + Math.random() * (b - a);

  const tex = (key: string): string => (scene.textures.exists(key) ? key : fallbackKey());
  function fallbackKey(): string {
    const k = 'fx_fallback_dot';
    if (!scene.textures.exists(k)) {
      const g = scene.make.graphics({}, false);
      g.fillStyle(0xffffff, 1).fillCircle(8, 8, 8);
      g.generateTexture(k, 16, 16);
      g.destroy();
    }
    return k;
  }

  // ───────────── particles ─────────────
  const live: Particle[] = [];
  const free: Phaser.GameObjects.Image[] = [];

  function emit(s: PSpec): void {
    if (live.length >= MAX_PARTICLES) {
      // Recycle the oldest particle rather than dropping new effects.
      const old = live.shift()!;
      old.img.setVisible(false);
      free.push(old.img);
    }
    const img = free.pop() ?? scene.add.image(0, 0, tex(s.key));
    img.setTexture(tex(s.key), s.frame as string | number | undefined);
    img.setPosition(s.x, s.y).setVisible(true).setActive(true);
    img.setBlendMode(s.add === false ? Phaser.BlendModes.NORMAL : Phaser.BlendModes.ADD);
    img.setDepth(s.depth ?? (s.add === false ? DEPTH.FX : DEPTH.FX_ADD));
    img.setRotation(s.rot ?? 0);
    img.setOrigin(0.5);
    if (s.tint !== undefined) img.setTint(s.tint);
    else img.clearTint();
    const s0 = s.s0 ?? 1;
    img.setScale(s0).setAlpha(s.a0 ?? 1);
    live.push({
      img,
      life: Math.max(0.01, s.life),
      age: 0,
      vx: s.vx ?? 0,
      vy: s.vy ?? 0,
      drag: s.drag ?? 0,
      gravity: s.gravity ?? 0,
      s0,
      s1: s.s1 ?? s0,
      a0: s.a0 ?? 1,
      a1: s.a1 ?? 0,
      spin: s.spin ?? 0,
      stretch: s.stretch ?? 0,
      frames: s.frames ?? 0,
      sx: 1,
      sy: 1,
    });
  }

  function updateParticles(dt: number): void {
    for (let i = live.length - 1; i >= 0; i--) {
      const p = live[i];
      p.age += dt;
      const t = p.age / p.life;
      if (t >= 1) {
        p.img.setVisible(false).setActive(false);
        free.push(p.img);
        live.splice(i, 1);
        continue;
      }
      if (p.drag) {
        const k = Math.exp(-p.drag * dt);
        p.vx *= k;
        p.vy *= k;
      }
      p.vy += p.gravity * dt;
      p.img.x += p.vx * dt;
      p.img.y += p.vy * dt;
      const sc = p.s0 + (p.s1 - p.s0) * t;
      if (p.stretch) {
        const sp = Math.hypot(p.vx, p.vy);
        p.img.setRotation(Math.atan2(p.vy, p.vx));
        p.img.setScale(sc * (1 + (sp / 400) * p.stretch), sc);
      } else {
        p.img.setScale(sc * p.sx, sc * p.sy);
        if (p.spin) p.img.rotation += p.spin * dt;
      }
      p.img.setAlpha(p.a0 + (p.a1 - p.a0) * t);
      if (p.frames > 1) p.img.setFrame(Math.min(p.frames - 1, Math.floor(t * p.frames)));
    }
  }

  // ───────────── lightning ─────────────
  const bolts: Bolt[] = [];
  const freeGfx: Phaser.GameObjects.Graphics[] = [];
  const takeGfx = (depth: number, add: boolean): Phaser.GameObjects.Graphics => {
    const g = freeGfx.pop() ?? scene.add.graphics();
    g.clear().setVisible(true).setAlpha(1).setDepth(depth);
    g.setBlendMode(add ? Phaser.BlendModes.ADD : Phaser.BlendModes.NORMAL);
    return g;
  };
  const releaseGfx = (g: Phaser.GameObjects.Graphics) => {
    g.clear().setVisible(false);
    freeGfx.push(g);
  };

  function jagged(a: Vec, b: Vec, jitter: number): Vec[] {
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    const n = Math.max(2, Math.round(len / 22));
    const nx = -(b.y - a.y) / (len || 1);
    const ny = (b.x - a.x) / (len || 1);
    const pts: Vec[] = [a];
    for (let i = 1; i < n; i++) {
      const t = i / n;
      const off = (Math.random() * 2 - 1) * jitter * Math.sin(Math.PI * t);
      pts.push({ x: a.x + (b.x - a.x) * t + nx * off, y: a.y + (b.y - a.y) * t + ny * off });
    }
    pts.push(b);
    return pts;
  }

  function drawBolt(b: Bolt): void {
    const g = b.g;
    g.clear();
    const fade = 1 - b.age / b.life;
    for (let i = 0; i < b.points.length - 1; i++) {
      const seg = jagged(b.points[i], b.points[i + 1], b.jitter);
      for (const [w, a, c] of [
        [b.width * 4, 0.18, b.color],
        [b.width * 2, 0.45, b.color],
        [Math.max(1.5, b.width * 0.7), 1, 0xffffff],
      ] as const) {
        g.lineStyle(w, c, a * fade);
        g.beginPath();
        g.moveTo(seg[0].x, seg[0].y);
        for (let k = 1; k < seg.length; k++) g.lineTo(seg[k].x, seg[k].y);
        g.strokePath();
      }
      if (b.branches && Math.random() < 0.5) {
        const m = seg[Math.floor(seg.length / 2)];
        const ang = Math.random() * Math.PI * 2;
        const end = { x: m.x + Math.cos(ang) * 40, y: m.y + Math.sin(ang) * 40 };
        const br = jagged(m, end, b.jitter * 0.6);
        g.lineStyle(Math.max(1, b.width * 0.6), b.color, 0.7 * fade);
        g.beginPath();
        g.moveTo(br[0].x, br[0].y);
        for (let k = 1; k < br.length; k++) g.lineTo(br[k].x, br[k].y);
        g.strokePath();
      }
    }
  }

  // ───────────── telegraphs ─────────────
  const teles: Tele[] = [];

  function makeTele(kind: TeleKind, dur: number, color: number, p: Partial<Tele['p']>): Tele {
    const t: Tele = {
      kind,
      g: kind === 'marker' ? null : takeGfx(DEPTH.TELEGRAPH, false),
      img: null,
      p: { x: 0, y: 0, x2: 0, y2: 0, radius: 0, w: 0, h: 0, angle: 0, width: 6, ...p },
      color,
      age: 0,
      dur: Math.max(0.05, dur / 1000),
      dead: false,
      get progress() {
        return Math.min(1, this.age / this.dur);
      },
      get done() {
        return this.dead || this.age >= this.dur;
      },
      cancel() {
        killTele(this);
      },
      update(params) {
        Object.assign(this.p, params);
      },
    };
    teles.push(t);
    return t;
  }

  function killTele(t: Tele): void {
    if (t.dead) return;
    t.dead = true;
    if (t.g) releaseGfx(t.g);
    t.img?.destroy();
    t.g = null;
    t.img = null;
  }

  function drawTele(t: Tele): void {
    const pr = t.progress;
    const pulse = 0.55 + 0.45 * Math.sin(t.age * 22);
    const late = pr > 0.8;
    const g = t.g;
    const c = t.color;
    if (t.kind === 'line' && g) {
      const { x, y, x2, y2, width } = t.p;
      const dx = x2 - x;
      const dy = y2 - y;
      const len = Math.hypot(dx, dy) || 1;
      const ux = dx / len;
      const uy = dy / len;
      g.clear();
      g.lineStyle(late ? width * 1.6 : 2, c, late ? 0.55 + 0.4 * pulse : 0.28);
      g.lineBetween(x, y, x2, y2);
      // Fill creeping from the origin.
      g.lineStyle(Math.max(2, width * 0.6), c, 0.75);
      g.lineBetween(x, y, x + dx * pr, y + dy * pr);
      // Chevrons travelling toward the target.
      g.lineStyle(3, c, 0.85);
      const step = 70;
      const off = (t.age * 260) % step;
      for (let d = off; d < len - 10; d += step) {
        const cx = x + ux * d;
        const cy = y + uy * d;
        const s = 9;
        g.beginPath();
        g.moveTo(cx - ux * s - uy * s, cy - uy * s + ux * s);
        g.lineTo(cx, cy);
        g.lineTo(cx - ux * s + uy * s, cy - uy * s - ux * s);
        g.strokePath();
      }
    } else if (t.kind === 'circle' && g) {
      const { x, y, radius } = t.p;
      g.clear();
      g.fillStyle(c, 0.12 + 0.18 * pr);
      g.fillCircle(x, y, radius * pr);
      g.lineStyle(late ? 5 : 3, c, late ? 0.6 + 0.4 * pulse : 0.7);
      g.strokeCircle(x, y, radius);
      // Contracting outer ring.
      g.lineStyle(2, c, 0.5 * (1 - pr));
      g.strokeCircle(x, y, radius * (1.6 - 0.6 * pr));
      g.lineStyle(2, c, 0.9);
      g.lineBetween(x - 10, y, x + 10, y);
      g.lineBetween(x, y - 10, x, y + 10);
    } else if (t.kind === 'rect' && g) {
      const { x, y, w, h } = t.p;
      g.clear();
      g.fillStyle(c, 0.1 + 0.15 * pr);
      g.fillRect(x, y, w, h);
      g.fillStyle(c, 0.35);
      g.fillRect(x, y, w * pr, h);
      // Hazard stripes along the top edge.
      g.fillStyle(c, 0.8);
      const stripe = 24;
      const shift = (t.age * 60) % (stripe * 2);
      for (let sx = x - stripe * 2 + shift; sx < x + w; sx += stripe * 2) {
        const a = Math.max(x, sx);
        const b = Math.min(x + w, sx + stripe);
        if (b > a) g.fillRect(a, y, b - a, Math.min(8, h));
      }
      g.lineStyle(late ? 4 : 2, c, late ? 0.6 + 0.4 * pulse : 0.7);
      g.strokeRect(x, y, w, h);
    } else if (t.kind === 'marker') {
      if (!t.img) {
        const key = scene.textures.exists(FX_EXTRA.alert) ? FX_EXTRA.alert : tex(TEX.chevron);
        t.img = scene.add.image(t.p.x, t.p.y, key).setDepth(DEPTH.TELEGRAPH).setTint(c);
      }
      let x = t.p.x;
      let y = t.p.y;
      const f = t.follow as unknown as { x?: number; y?: number; active?: boolean } | undefined;
      if (f && f.active !== false && typeof f.x === 'number' && typeof f.y === 'number') {
        x = f.x;
        y = f.y - 70;
      }
      t.img.setPosition(x, y - 6 * Math.abs(Math.sin(t.age * 10)));
      t.img.setScale(0.9 + 0.25 * pulse).setAlpha(0.6 + 0.4 * pulse);
    }
  }

  // ───────────── numbers / text ─────────────
  const nums: Num[] = [];
  const freeText: Phaser.GameObjects.Text[] = [];
  function spawnText(x: number, y: number, str: string, color: number, size: number, life: number): void {
    if (nums.length > 60) {
      const old = nums.shift()!;
      old.t.setVisible(false);
      freeText.push(old.t);
    }
    const t =
      freeText.pop() ??
      scene.add.text(0, 0, '', {
        fontFamily: FONT.display,
        fontStyle: '700',
        stroke: '#05070b',
        strokeThickness: 5,
      });
    t.setText(str).setFontSize(size).setColor(css(color)).setOrigin(0.5).setPosition(x, y);
    t.setDepth(DEPTH.WORLD_UI).setVisible(true).setAlpha(1);
    nums.push({ t, age: 0, life, vy: -90, baseScale: 1 });
  }

  // ───────────── hit flash ─────────────
  interface Flashed {
    obj: Phaser.GameObjects.Image;
    until: number;
    tinted: boolean;
    tint: number;
  }
  const flashed = new Map<Phaser.GameObjects.Image, Flashed>();
  let clock = 0;

  function flashObj(o: Phaser.GameObjects.GameObject, ms: number): void {
    const img = o as Phaser.GameObjects.Image;
    if (!img || !img.active || typeof img.setTintFill !== 'function') return;
    let rec = flashed.get(img);
    if (!rec) {
      rec = { obj: img, until: 0, tinted: img.isTinted, tint: img.tintTopLeft };
      flashed.set(img, rec);
    }
    rec.until = clock + ms / 1000;
    if (settings().reducedFlashing) img.setTint(0xffb0b0);
    else img.setTintFill(0xffffff);
  }

  // ───────────── screen flash / shake ─────────────
  let overlay: Phaser.GameObjects.Rectangle | null = null;
  let overlayAge = 0;
  let overlayLife = 0;
  let overlayAlpha = 0;
  let shakeUntil = 0;
  let shakeIntensity = 0;

  const fx: FxApi = {
    muzzleFlash(x, y, angle, color = COLORS.electric, scale = 1) {
      emit({ key: TEX.muzzle, x, y, rot: angle, life: rand(0.045, 0.07), s0: scale * rand(0.8, 1.05), s1: scale * 1.15, a0: 1, a1: 0, tint: mix(color, 0xffffff, 0.35) });
      emit({ key: TEX.soft, x, y, life: 0.08, s0: 0.9 * scale, s1: 1.4 * scale, a0: 0.7, a1: 0, tint: color });
    },

    sparks(x, y, angle, opts = {}) {
      const n = opts.count ?? 6;
      const spread = opts.spread ?? 0.9;
      const speed = opts.speed ?? 520;
      const color = opts.color ?? COLORS.telegraph;
      for (let i = 0; i < n; i++) {
        const a = angle + rand(-spread, spread);
        const sp = speed * rand(0.4, 1.1);
        emit({ key: TEX.spark, x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: (opts.life ?? 0.28) * rand(0.6, 1.2), s0: rand(0.6, 1), s1: 0.2, a0: 1, a1: 0, tint: color, drag: 4, gravity: 600, stretch: 0.6 });
      }
    },

    impact(x, y, angle, kind, color) {
      const back = angle + Math.PI;
      const presets = {
        bullet: { n: 5, sp: 420, c: color ?? 0xfff2c0, glow: 0.6 },
        pellet: { n: 3, sp: 360, c: color ?? 0xffe0a0, glow: 0.45 },
        heavy: { n: 10, sp: 620, c: color ?? 0xffd070, glow: 1.1 },
        energy: { n: 7, sp: 480, c: color ?? COLORS.electric, glow: 0.9 },
        shield: { n: 8, sp: 380, c: color ?? COLORS.shield, glow: 1 },
      } as const;
      const p = presets[kind] ?? presets.bullet;
      fx.sparks(x, y, back, { count: p.n, speed: p.sp, color: p.c, spread: 0.85 });
      emit({ key: TEX.soft, x, y, life: 0.09, s0: 0.5 * p.glow, s1: 0.9 * p.glow, a0: 0.9, a1: 0, tint: p.c });
      if (kind === 'heavy' || kind === 'shield') emit({ key: FX_EXTRA.star, x, y, life: 0.1, s0: 0.6, s1: 1, a0: 1, a1: 0, tint: p.c, rot: Math.random() });
    },

    explosion(x, y, radius, color = COLORS.explosive) {
      const s = radius / 64;
      emit({ key: TEX.soft, x, y, life: 0.14, s0: s * 1.6, s1: s * 2.4, a0: 1, a1: 0, tint: 0xffffff });
      emit({ key: TEX.explosion, frame: 0, x, y, life: 0.42, s0: s * 1.05, s1: s * 1.25, a0: 1, a1: 0.6, tint: color, frames: 8, rot: Math.random() * Math.PI * 2 });
      fx.ring(x, y, radius * 1.1, { color, duration: 280, width: 6, fromRadius: radius * 0.3 });
      const n = Math.min(14, 5 + Math.round(radius / 18));
      for (let i = 0; i < n; i++) {
        const a = Math.random() * Math.PI * 2;
        const sp = rand(120, 380) * (0.6 + s * 0.4);
        emit({ key: TEX.ember, x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 120, life: rand(0.35, 0.8), s0: rand(0.8, 1.6), s1: 0.3, a0: 1, a1: 0, tint: mix(color, 0xffffff, 0.3), drag: 2.5, gravity: 500 });
      }
      fx.smoke(x, y, { count: Math.min(8, 3 + Math.round(radius / 30)), scale: s });
      fx.debris(x, y, { count: Math.min(8, 2 + Math.round(radius / 30)) });
    },

    lightning(points, opts = {}) {
      if (points.length < 2) return;
      const b: Bolt = {
        g: takeGfx(DEPTH.FX_ADD, true),
        points: points.map((p) => ({ x: p.x, y: p.y })),
        color: opts.color ?? COLORS.electric,
        width: opts.width ?? 3,
        life: (opts.duration ?? 180) / 1000,
        age: 0,
        jitter: opts.jitter ?? 16,
        branches: opts.branches ?? true,
        nextJitter: 0,
      };
      bolts.push(b);
      drawBolt(b);
      for (const p of points) emit({ key: TEX.soft, x: p.x, y: p.y, life: 0.12, s0: 0.5, s1: 0.8, a0: 0.8, a1: 0, tint: b.color });
    },

    beam(x0, y0, x1, y1, opts = {}) {
      fx.lightning([{ x: x0, y: y0 }, { x: x1, y: y1 }], { color: opts.color ?? 0xffffff, width: opts.width ?? 5, duration: opts.duration ?? 120, jitter: 1, branches: false });
    },

    ring(x, y, radius, opts = {}) {
      const from = opts.fromRadius ?? radius * 0.2;
      emit({ key: TEX.ring, x, y, life: (opts.duration ?? 300) / 1000, s0: from / 64, s1: radius / 64, a0: 0.95, a1: 0, tint: opts.color ?? 0xffffff });
    },

    smoke(x, y, opts = {}) {
      const n = opts.count ?? 4;
      const sc = opts.scale ?? 1;
      for (let i = 0; i < n; i++) {
        emit({ key: TEX.smoke, add: false, x: x + rand(-14, 14) * sc, y: y + rand(-10, 10) * sc, vx: rand(-40, 40), vy: rand(-80, -20), life: rand(0.6, 1.1), s0: rand(0.5, 0.8) * sc, s1: rand(1.2, 1.8) * sc, a0: 0.45, a1: 0, tint: opts.color ?? 0x3a3f4a, spin: rand(-1, 1), drag: 1.5 });
      }
    },

    embers(x, y, opts = {}) {
      for (let i = 0; i < (opts.count ?? 5); i++) {
        emit({ key: TEX.ember, x: x + rand(-10, 10), y: y + rand(-6, 6), vx: rand(-40, 40), vy: rand(-160, -60), life: rand(0.4, 0.9), s0: rand(0.6, 1.1), s1: 0.2, a0: 1, a1: 0, tint: opts.color ?? COLORS.burn, drag: 1 });
      }
    },

    debris(x, y, opts = {}) {
      const sp = opts.speed ?? 360;
      for (let i = 0; i < (opts.count ?? 6); i++) {
        const a = rand(-Math.PI, 0);
        emit({ key: TEX.shard, add: false, x, y, vx: Math.cos(a) * sp * rand(0.4, 1), vy: Math.sin(a) * sp * rand(0.5, 1.1), life: rand(0.5, 0.9), s0: rand(0.8, 1.4), s1: 0.6, a0: 1, a1: 0, tint: opts.color ?? COLORS.steel3, spin: rand(-12, 12), gravity: 1400, drag: 0.5 });
      }
    },

    vent(x, y, angle) {
      for (let i = 0; i < 6; i++) {
        const a = angle - Math.PI / 2 + rand(-0.5, 0.5);
        emit({ key: TEX.smoke, add: false, x, y, vx: Math.cos(a) * rand(60, 160), vy: Math.sin(a) * rand(60, 160), life: rand(0.5, 0.9), s0: 0.25, s1: rand(0.8, 1.2), a0: 0.55, a1: 0, tint: 0xd8e0ea, drag: 2.2 });
      }
    },

    damageNumber(x, y, amount, opts = {}) {
      if (!settings().damageNumbers && !opts.player) return;
      const v = Math.max(1, Math.round(amount));
      const color = opts.player ? COLORS.hp : (opts.color ?? (opts.crit ? COLORS.explosive : COLORS.uiText));
      spawnText(x + rand(-12, 12), y - 10, opts.crit ? `${v}!` : `${v}`, color, opts.crit ? 36 : 26, 0.7);
      if (opts.crit) nums[nums.length - 1].baseScale = 1.25;
    },

    floatText(x, y, text, opts = {}) {
      spawnText(x, y, text, opts.color ?? COLORS.uiText, opts.size ?? 26, (opts.duration ?? 1100) / 1000);
    },

    shake(intensity, durationMs) {
      const k = settings().screenShake;
      if (k <= 0) return;
      const amount = intensity * k;
      const cam = scene.cameras.main;
      const now = clock;
      const stronger = amount >= shakeIntensity || now > shakeUntil;
      if (!stronger) return;
      shakeIntensity = amount;
      shakeUntil = now + durationMs / 1000;
      cam.shake(durationMs, amount, true);
    },

    flash(color, alpha, durationMs) {
      const a = settings().reducedFlashing ? Math.min(alpha, 0.12) * 0.6 : alpha;
      if (!overlay || !overlay.scene) {
        overlay = scene.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, color).setOrigin(0).setScrollFactor(0).setDepth(DEPTH.WORLD_UI + 50);
      }
      overlay.setFillStyle(color).setVisible(true);
      overlayAlpha = Math.max(a, overlay.visible ? overlay.alpha : 0);
      overlay.setAlpha(overlayAlpha);
      overlayAge = 0;
      overlayLife = durationMs / 1000;
    },

    hitFlash(target, durationMs = 60) {
      const anyT = target as unknown as { list?: Phaser.GameObjects.GameObject[] };
      if (Array.isArray(anyT.list)) for (const c of anyT.list) flashObj(c, durationMs);
      else flashObj(target, durationMs);
    },

    afterimage(source, opts = {}) {
      const color = opts.color ?? COLORS.electric;
      const life = (opts.duration ?? 220) / 1000;
      const alpha = opts.alpha ?? 0.5;
      const list: Phaser.GameObjects.GameObject[] = (source as Phaser.GameObjects.Container).list ?? [source];
      const stack = [...list];
      while (stack.length) {
        const o = stack.shift() as Phaser.GameObjects.Image & { list?: Phaser.GameObjects.GameObject[] };
        if (!o || !o.visible) continue;
        if (Array.isArray(o.list)) {
          stack.push(...o.list);
          continue;
        }
        if (!o.texture || o.texture.key === '__MISSING' || o.alpha <= 0.05) continue;
        const d = o.getWorldTransformMatrix().decomposeMatrix() as { translateX: number; translateY: number; rotation: number; scaleX: number; scaleY: number };
        emit({ key: o.texture.key, frame: o.frame.name, x: d.translateX, y: d.translateY, rot: d.rotation, life, s0: 1, s1: 1, a0: alpha, a1: 0, tint: color, depth: DEPTH.PLAYER - 1 });
        const p = live[live.length - 1];
        p.img.setOrigin(o.originX, o.originY);
        p.sx = d.scaleX;
        p.sy = d.scaleY;
      }
    },

    trailSegment(x0, y0, x1, y1, kind: TrailKind, width = 1) {
      const len = Math.hypot(x1 - x0, y1 - y0);
      const n = Math.max(1, Math.round(len / 18));
      if (kind === 'electric') {
        fx.lightning([{ x: x0, y: y0 }, { x: x1, y: y1 }], { color: COLORS.electric, width: 2 * width, duration: 160, jitter: 10, branches: false });
        return;
      }
      for (let i = 0; i <= n; i++) {
        const t = i / n;
        const x = x0 + (x1 - x0) * t;
        const y = y0 + (y1 - y0) * t;
        if (kind === 'burn') emit({ key: FX_EXTRA.flame, x, y, vy: -60, life: rand(0.3, 0.55), s0: 0.9 * width, s1: 0.3, a0: 1, a1: 0, tint: COLORS.burn });
        else if (kind === 'plasma') emit({ key: TEX.soft, x, y, life: 0.3, s0: 0.45 * width, s1: 0.2, a0: 0.8, a1: 0, tint: 0xb9a8ff });
        else emit({ key: TEX.smoke, add: false, x, y, life: 0.5, s0: 0.4 * width, s1: 0.9, a0: 0.6, a1: 0, tint: 0x241838 });
      }
    },

    warnLine(x0, y0, x1, y1, durationMs, opts = {}) {
      return makeTele('line', durationMs, opts.color ?? COLORS.threat, { x: x0, y: y0, x2: x1, y2: y1, width: opts.width ?? 6 });
    },
    warnCircle(x, y, radius, durationMs, opts = {}) {
      return makeTele('circle', durationMs, opts.color ?? COLORS.threat, { x, y, radius });
    },
    warnRect(x, y, w, h, durationMs, opts = {}) {
      return makeTele('rect', durationMs, opts.color ?? COLORS.threat, { x, y, w, h });
    },
    warnMarker(x, y, durationMs, opts = {}) {
      const t = makeTele('marker', durationMs, opts.color ?? COLORS.telegraph, { x, y });
      t.follow = opts.follow;
      return t;
    },

    spawnPortal(x, y, durationMs, opts = {}) {
      const c = opts.color ?? COLORS.threat;
      const r = opts.radius ?? 56;
      const life = durationMs / 1000;
      emit({ key: TEX.spawnPortal, x, y, life, s0: 0.2, s1: r / 64, a0: 0.4, a1: 1, tint: c, spin: 3 });
      emit({ key: TEX.soft, x, y, life, s0: 0.3, s1: (r / 64) * 1.6, a0: 0.2, a1: 0.8, tint: c });
      for (let i = 0; i < 10; i++) {
        const a = (i / 10) * Math.PI * 2;
        emit({ key: TEX.ember, x: x + Math.cos(a) * r * 1.4, y: y + Math.sin(a) * r * 1.4, vx: -Math.cos(a) * r * 1.4 / life, vy: -Math.sin(a) * r * 1.4 / life, life, s0: 1, s1: 0.4, a0: 0.2, a1: 1, tint: c });
      }
      scene.time.delayedCall(durationMs, () => {
        if (!scene.sys.isActive()) return;
        fx.ring(x, y, r * 1.6, { color: c, duration: 240, fromRadius: r * 0.4 });
        emit({ key: TEX.soft, x, y, life: 0.2, s0: (r / 64) * 2, s1: (r / 64) * 2.6, a0: 0.9, a1: 0, tint: 0xffffff });
      });
    },

    update(dt) {
      clock += dt;
      updateParticles(dt);

      for (let i = bolts.length - 1; i >= 0; i--) {
        const b = bolts[i];
        b.age += dt;
        if (b.age >= b.life) {
          releaseGfx(b.g);
          bolts.splice(i, 1);
          continue;
        }
        b.nextJitter -= dt;
        if (b.nextJitter <= 0) {
          b.nextJitter = 0.05;
          drawBolt(b);
        } else b.g.setAlpha(1 - b.age / b.life);
      }

      for (let i = teles.length - 1; i >= 0; i--) {
        const t = teles[i];
        if (t.dead) {
          teles.splice(i, 1);
          continue;
        }
        t.age += dt;
        if (t.age >= t.dur) {
          killTele(t);
          teles.splice(i, 1);
          continue;
        }
        drawTele(t);
      }

      for (let i = nums.length - 1; i >= 0; i--) {
        const n = nums[i];
        n.age += dt;
        const t = n.age / n.life;
        if (t >= 1) {
          n.t.setVisible(false);
          freeText.push(n.t);
          nums.splice(i, 1);
          continue;
        }
        n.t.y += n.vy * dt;
        n.vy *= Math.exp(-3 * dt);
        const pop = t < 0.12 ? 1 + (0.12 - t) * 4 : 1;
        n.t.setScale(n.baseScale * pop).setAlpha(t > 0.6 ? 1 - (t - 0.6) / 0.4 : 1);
      }

      for (const [img, rec] of flashed) {
        if (!img.active || !img.scene) {
          flashed.delete(img);
          continue;
        }
        if (clock >= rec.until) {
          if (rec.tinted) img.setTint(rec.tint);
          else img.clearTint();
          flashed.delete(img);
        }
      }

      if (overlay && overlay.visible) {
        overlayAge += dt;
        const t = overlayLife > 0 ? overlayAge / overlayLife : 1;
        if (t >= 1) overlay.setVisible(false);
        else overlay.setAlpha(overlayAlpha * (1 - t));
      }
    },

    destroy() {
      for (const p of live) p.img.destroy();
      live.length = 0;
      for (const i of free) i.destroy();
      free.length = 0;
      for (const b of bolts) b.g.destroy();
      bolts.length = 0;
      for (const t of teles) killTele(t);
      teles.length = 0;
      for (const g of freeGfx) g.destroy();
      freeGfx.length = 0;
      for (const n of nums) n.t.destroy();
      nums.length = 0;
      for (const t of freeText) t.destroy();
      freeText.length = 0;
      flashed.clear();
      overlay?.destroy();
      overlay = null;
    },
  };

  return fx;
}
